import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  FiSearch, FiXCircle, FiX, FiCheckCircle, FiShoppingCart,
  FiPlus, FiArrowLeft, FiRefreshCw,
} from 'react-icons/fi';
import api from '../services/api';
import { useAuth } from '../context/AuthContext';
import { fmtFecha, fmtMoney, fmtHora, localDate } from '../utils/format';
import { exportarExcel, exportarPDF } from '../utils/exportar';
import { buildOpciones, opcionDefault, precioReferencial } from '../utils/presentaciones';
import '../styles/Ventas.css';

/* ─────────────────────────────────────────────────────────────────────────────
   Helpers compartidos
───────────────────────────────────────────────────────────────────────────── */
const hoy = () => localDate();

function addDias(fechaStr, dias) {
  const d = new Date(fechaStr + 'T00:00:00');
  d.setDate(d.getDate() + dias);
  return `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,'0')}-${String(d.getDate()).padStart(2,'0')}`;
}

const cartKey   = (producto_id, almacen_id) => `${producto_id}_${almacen_id}`;

/* Iconos SVG inline (Feather) — evitan problemas de render de react-icons */
const svgProps = (size) => ({
  xmlns: 'http://www.w3.org/2000/svg', viewBox: '0 0 24 24',
  width: size, height: size, fill: 'none', stroke: 'currentColor',
  strokeWidth: 2, strokeLinecap: 'round', strokeLinejoin: 'round',
  style: { display: 'block' },
});
const IconPrint = ({ size = 14 }) => (
  <svg {...svgProps(size)}>
    <polyline points="6 9 6 2 18 2 18 9" />
    <path d="M6 18H4a2 2 0 0 1-2-2v-5a2 2 0 0 1 2-2h16a2 2 0 0 1 2 2v5a2 2 0 0 1-2 2h-2" />
    <rect x="6" y="14" width="12" height="8" />
  </svg>
);
const IconTruck = ({ size = 14 }) => (
  <svg {...svgProps(size)}>
    <rect x="1" y="3" width="15" height="13" />
    <polygon points="16 8 20 8 23 11 23 16 16 16 16 8" />
    <circle cx="5.5" cy="18.5" r="2.5" />
    <circle cx="18.5" cy="18.5" r="2.5" />
  </svg>
);
const IconSlash = ({ size = 14 }) => (
  <svg {...svgProps(size)}>
    <circle cx="12" cy="12" r="10" />
    <line x1="4.93" y1="4.93" x2="19.07" y2="19.07" />
  </svg>
);
const IconDoc = ({ size = 14 }) => (
  <svg {...svgProps(size)}>
    <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
    <polyline points="14 2 14 8 20 8" />
    <line x1="16" y1="13" x2="8" y2="13" />
    <line x1="16" y1="17" x2="8" y2="17" />
    <polyline points="10 9 9 9 8 9" />
  </svg>
);
const IconEye = ({ size = 14 }) => (
  <svg {...svgProps(size)}>
    <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
    <circle cx="12" cy="12" r="3" />
  </svg>
);

const almacenTag = (nombre) => {
  if (!nombre) return '?';
  const words = nombre.trim().toUpperCase().split(/[\s_]+/).filter(Boolean);
  if (words.length === 1) return words[0].slice(0, 4);
  return words.map(w => w[0]).join('').slice(0, 3);
};

/* Próximo número correlativo de una serie (secuencia = próximo a emitir) */
const proxNumero = (s) => {
  const n = parseInt(s?.sequence, 10);
  if (Number.isNaN(n)) return `${s?.serie || ''}-…`;
  return `${s.serie}-${String(n).padStart(7, '0')}`;
};


/* ─────────────────────────────────────────────────────────────────────────────
   Modal Ticket de éxito
───────────────────────────────────────────────────────────────────────────── */
function TicketModal({ venta, onNuevaVenta, onVerVentas }) {
  const abrirReporte = (fmt) => {
    window.open(`/print-venta/${venta.id}?fmt=${fmt}`, '_blank', 'noopener');
  };

  return (
    <div className="vta-overlay">
      <div className="vta-ticket-modal" onClick={e => e.stopPropagation()}>
        <div className="vta-ticket-icon"><FiCheckCircle size={32} /></div>
        <div className="vta-ticket-title">Venta completada</div>
        <div className="vta-ticket-numero">{venta.numero}</div>
        <div className="vta-ticket-total">S/ {fmtMoney(venta.total)}</div>
        <div className="vta-ticket-total-label">Total cobrado</div>
        <div className="vta-ticket-detail">
          <div className="vta-ticket-row">
            <span>Cliente</span>
            <strong>{venta.cliente_nombre || 'Clientes Varios'}</strong>
          </div>
          <div className="vta-ticket-row">
            <span>Subtotal</span>
            <strong>S/ {fmtMoney(venta.subtotal)}</strong>
          </div>
          {venta.descuento > 0 && (
            <div className="vta-ticket-row">
              <span>Descuento</span>
              <strong>- S/ {fmtMoney(venta.descuento)}</strong>
            </div>
          )}
          <div className="vta-ticket-row">
            <span>IGV</span>
            <strong>S/ {fmtMoney(venta.igv)}</strong>
          </div>
          <div className="vta-ticket-row">
            <span>Tipo de pago</span>
            <strong>{venta.condicion_pago}</strong>
          </div>
          {venta.forma_pago && (
            <div className="vta-ticket-row">
              <span>Forma de pago</span>
              <strong>{venta.forma_pago}</strong>
            </div>
          )}
          {venta.documento && (
            <div className="vta-ticket-row">
              <span>Documento</span>
              <strong>{venta.documento}</strong>
            </div>
          )}
          {venta.condicion_pago === 'MIXTO' && (
            <>
              <div className="vta-ticket-row">
                <span>Pagado al contado</span>
                <strong>S/ {fmtMoney(venta.monto_contado)}</strong>
              </div>
              <div className="vta-ticket-row">
                <span>Saldo en crédito</span>
                <strong>S/ {fmtMoney((parseFloat(venta.total) || 0) - (parseFloat(venta.monto_contado) || 0))}</strong>
              </div>
            </>
          )}
          {venta.condicion_pago !== 'CONTADO' && venta.fecha_vencimiento && (
            <div className="vta-ticket-row">
              <span>Vence el</span>
              <strong>{fmtFecha(venta.fecha_vencimiento)}</strong>
            </div>
          )}
        </div>

        {/* Botones de impresión */}
        <div className="vta-ticket-print-label">Imprimir comprobante:</div>
        <div className="vta-ticket-print-btns">
          <button className="vta-ticket-btn-print" onClick={() => abrirReporte('a4')}
            title="Imprimir en papel A4">
            <IconDoc size={14} /> A4
          </button>
          <button className="vta-ticket-btn-print" onClick={() => abrirReporte('80')}
            title="Imprimir en ticket 80mm">
            <IconPrint size={14} /> 80mm
          </button>
          <button className="vta-ticket-btn-print" onClick={() => abrirReporte('60')}
            title="Imprimir en ticket 60mm">
            <IconPrint size={14} /> 60mm
          </button>
        </div>

        <div className="vta-ticket-actions">
          <button className="vta-ticket-btn-nueva" onClick={onNuevaVenta}>Nueva Venta</button>
          <button className="vta-ticket-btn-ver"   onClick={onVerVentas}>Ver Ventas</button>
        </div>
      </div>
    </div>
  );
}

/* ─────────────────────────────────────────────────────────────────────────────
   Modal de confirmación de cobro — forma de pago + tipo de documento
───────────────────────────────────────────────────────────────────────────── */
function CobrarModal({
  total, condicion, clienteNombre,
  series, serieSelecta, onSelectSerie,
  formasPago, formaPago, onSelectFormaPago,
  montoContado, onMontoContado,
  err, cobrando, onConfirm, onClose,
}) {
  const saldoCredito = Math.max(0, (parseFloat(total) || 0) - (parseFloat(montoContado) || 0));
  return (
    <div className="vta-overlay">
      <div className="vta-cobro-modal" onClick={e => e.stopPropagation()}>
        <div className="vta-cobro-header">
          <span>Confirmar venta</span>
          <button className="vta-cobro-close" onClick={onClose} disabled={cobrando} title="Cancelar">
            <FiX size={16} />
          </button>
        </div>

        <div className="vta-cobro-total-label">Total a cobrar — {condicion}</div>
        <div className="vta-cobro-total">S/ {fmtMoney(total)}</div>
        <div className="vta-cobro-cliente">
          Cliente: <strong>{clienteNombre || 'Clientes Varios'}</strong>
        </div>

        {/* Tipo de documento + correlativo */}
        <div className="vta-cobro-section-label">Tipo de documento</div>
        <div className="vta-cobro-doc-row">
          <select
            className="vta-doc-selector"
            value={serieSelecta?.id || ''}
            disabled={cobrando}
            onChange={e => onSelectSerie(series.find(s => s.id === Number(e.target.value)) || null)}
          >
            <option value="">— Selecciona —</option>
            {series.map(s => (
              <option key={s.id} value={s.id}>
                {s.doc_name} — {s.serie}
              </option>
            ))}
          </select>
          {serieSelecta && (
            <span className="vta-cobro-correlativo" title="Número que se emitirá">
              {proxNumero(serieSelecta)}
            </span>
          )}
        </div>

        {/* Forma de pago — CONTADO y parte al contado del MIXTO */}
        {(condicion === 'CONTADO' || condicion === 'MIXTO') && (
          <>
            <div className="vta-cobro-section-label">
              {condicion === 'MIXTO' ? 'Forma de pago (monto al contado)' : 'Forma de pago'}
            </div>
            <div className="vta-fp-grid">
              {formasPago.length === 0 ? (
                <div className="vta-fp-empty">No hay formas de pago registradas en Configuración.</div>
              ) : formasPago.map(fp => (
                <button
                  key={fp.id}
                  type="button"
                  disabled={cobrando}
                  className={`vta-fp-btn${formaPago?.id === fp.id ? ' active' : ''}`}
                  onClick={() => onSelectFormaPago(fp)}
                >
                  {fp.name}
                </button>
              ))}
            </div>
          </>
        )}

        {/* MIXTO: monto al contado + saldo en crédito */}
        {condicion === 'MIXTO' && (
          <div className="vta-mixto-row">
            <label className="vta-mixto-field">
              <span>Monto al Contado</span>
              <input
                type="number" min="0.01" step="0.01" max={total}
                value={montoContado}
                disabled={cobrando}
                autoFocus
                placeholder="0.00"
                onChange={e => onMontoContado(e.target.value)}
              />
            </label>
            <label className="vta-mixto-field">
              <span>Saldo en Crédito</span>
              <input type="text" value={`S/ ${fmtMoney(saldoCredito)}`} readOnly disabled />
            </label>
          </div>
        )}

        {err && <div className="vta-error">{err}</div>}

        <div className="vta-cobro-question">¿Desea proceder con la venta?</div>
        <div className="vta-cobro-actions">
          <button className="vta-cobro-btn-cancel" onClick={onClose} disabled={cobrando}>
            Cancelar
          </button>
          <button className="vta-cobro-btn-ok" onClick={onConfirm} disabled={cobrando}>
            {cobrando ? 'Procesando...' : 'Sí, generar venta'}
          </button>
        </div>
      </div>
    </div>
  );
}

/* ─────────────────────────────────────────────────────────────────────────────
   Sin caja abierta
───────────────────────────────────────────────────────────────────────────── */
function SinCaja({ navigate }) {
  return (
    <div className="vta-no-caja">
      <FiXCircle size={64} className="vta-no-caja-icon" />
      <div className="vta-no-caja-title">No hay caja abierta</div>
      <div className="vta-no-caja-subtitle">
        Para realizar ventas debes abrir una caja primero
      </div>
      <button className="vta-btn-apertura" onClick={() => navigate('/caja/apertura')}>
        Ir a Apertura de Caja
      </button>
    </div>
  );
}

/* ═══════════════════════════════════════════════════════════════════════════════
   LISTA DE VENTAS DEL DÍA
═══════════════════════════════════════════════════════════════════════════════ */
function ListaVentas({ cajaInfo, onNuevaVenta }) {
  const navigate       = useNavigate();
  const { user }       = useAuth();
  const esAdmin        = !!(user?.perfil_es_admin || user?.rol === 'admin');

  const [rows,      setRows]      = useState([]);
  const [loading,   setLoading]   = useState(true);
  const [error,     setError]     = useState('');
  const [confirmVenta, setConfirmVenta] = useState(null); // { id, numero, total }
  const [anulando,  setAnulando]  = useState(false);
  const [errAnular, setErrAnular] = useState('');
  const [printVenta, setPrintVenta] = useState(null); // { id, numero }
  const [despachoVenta, setDespachoVenta] = useState(null); // { id, numero }
  const [detalleVenta, setDetalleVenta] = useState(null); // venta completa con líneas
  const [filtroCliente, setFiltroCliente] = useState('');
  const [desde, setDesde] = useState(hoy());
  const [hasta, setHasta] = useState(hoy());

  const verDetalle = async (id) => {
    try {
      const r = await api.get(`/api/ventas/${id}`);
      setDetalleVenta(r.data?.data || null);
    } catch (e) {
      setError(e.response?.data?.error || 'No se pudo cargar el detalle');
    }
  };

  const imprimir = (fmt) => {
    if (!printVenta) return;
    window.open(`/print-venta/${printVenta.id}?fmt=${fmt}`, '_blank', 'noopener');
    setPrintVenta(null);
  };

  const imprimirDespacho = (fmt) => {
    if (!despachoVenta) return;
    window.open(`/print-despacho/${despachoVenta.id}?fmt=${fmt}`, '_blank', 'noopener');
    setDespachoVenta(null);
  };

  const cargar = useCallback(async () => {
    setLoading(true); setError('');
    try {
      const r = await api.get('/api/ventas', { params: { desde, hasta } });
      setRows(r.data?.data || []);
    } catch (e) {
      setError(e.response?.data?.error || e.message);
    } finally {
      setLoading(false);
    }
  }, [desde, hasta]);

  const irAHoy = () => { setDesde(hoy()); setHasta(hoy()); };

  useEffect(() => { cargar(); }, [cargar]);

  const handleAnular = async () => {
    if (!confirmVenta) return;
    setAnulando(true); setErrAnular('');
    try {
      await api.delete(`/api/ventas/${confirmVenta.id}/anular`);
      setConfirmVenta(null);
      await cargar();
    } catch (e) {
      setErrAnular(e.response?.data?.error || e.message || 'Error al anular');
    } finally {
      setAnulando(false);
    }
  };

  /* Filtro por cliente (también acepta N° de venta o documento) */
  const q = filtroCliente.trim().toLowerCase();
  const rowsFiltradas = !q ? rows : rows.filter(r =>
    (r.cliente_nombre || 'clientes varios').toLowerCase().includes(q) ||
    String(r.cliente_documento || '').toLowerCase().includes(q) ||
    String(r.numero || '').toLowerCase().includes(q)
  );

  /* Exportación PDF / Excel (respeta el filtro activo) */
  const exportar = (tipo) => {
    if (!rowsFiltradas.length) return;
    const encabezados = ['N° Venta', 'Fecha / Hora', 'Caja', 'Almacén', 'Cliente', 'Items', 'Tipo', 'Total S/', 'Estado'];
    const base = rowsFiltradas.map(r => [
      r.numero,
      `${fmtFecha(r.fecha)} ${fmtHora(r.creado_en)}`,
      r.caja_nombre || '—',
      r.almacen_nombre || '—',
      r.cliente_nombre || 'Clientes Varios',
      Number(r.nro_items || 0),
      r.condicion_pago,
      Number(r.total || 0),
      r.estado,
    ]);
    const rango  = desde === hasta ? fmtFecha(desde) : `${fmtFecha(desde)} — ${fmtFecha(hasta)}`;
    const nombre = `ventas_${desde}_${hasta}`;
    if (tipo === 'pdf') {
      exportarPDF(
        `Ventas — ${rango}`, nombre, encabezados,
        base.map(f => f.map((v, i) => (i === 7 ? fmtMoney(v) : v))),
        q ? `Filtro: "${filtroCliente.trim()}"` : '',
      );
    } else {
      exportarExcel(nombre, encabezados, base);
    }
  };

  /* KPIs */
  const completadas  = rows.filter(r => r.estado === 'COMPLETADA');
  const totalDia     = completadas.reduce((s, r) => s + parseFloat(r.total || 0), 0);
  const totalContado = completadas.filter(r => r.condicion_pago === 'CONTADO')
                                  .reduce((s, r) => s + parseFloat(r.total || 0), 0);
  const totalCredito = completadas.filter(r => r.condicion_pago !== 'CONTADO')
                                  .reduce((s, r) => s + parseFloat(r.total || 0), 0);

  const estadoBadge = (estado) => {
    if (estado === 'COMPLETADA') return { label: 'COMPLETADA', cls: 'vta-badge-ok'   };
    if (estado === 'ANULADA')    return { label: 'ANULADA',    cls: 'vta-badge-null' };
    return { label: estado, cls: '' };
  };

  return (
    <div className="vta-page">

      {/* ── Header ── */}
      <div className="vta-lista-header">
        <div className="vta-lista-header-left">
          <FiShoppingCart size={16} />
          <span className="vta-lista-title">
            Ventas — {desde === hasta ? fmtFecha(desde) : `${fmtFecha(desde)} — ${fmtFecha(hasta)}`}
          </span>
          {cajaInfo && <span className="vta-header-badge">SESIÓN ABIERTA</span>}
        </div>
        <div className="vta-lista-header-right">
          <div className="vta-lista-fechas">
            <input
              type="date"
              value={desde}
              max={hasta}
              onChange={e => setDesde(e.target.value)}
              title="Desde"
            />
            <span>—</span>
            <input
              type="date"
              value={hasta}
              min={desde}
              max={hoy()}
              onChange={e => setHasta(e.target.value)}
              title="Hasta"
            />
            {(desde !== hoy() || hasta !== hoy()) && (
              <button className="vta-lista-fechas-hoy" onClick={irAHoy} title="Volver a hoy">
                Hoy
              </button>
            )}
          </div>
          <div className="vta-lista-search">
            <FiSearch size={13} />
            <input
              placeholder="Buscar cliente, N° o doc..."
              value={filtroCliente}
              onChange={e => setFiltroCliente(e.target.value)}
            />
            {filtroCliente && (
              <button className="vta-lista-search-clear" title="Limpiar" onClick={() => setFiltroCliente('')}>
                <FiX size={13} />
              </button>
            )}
          </div>
          <button className="vta-btn-export" onClick={() => exportar('pdf')}
            disabled={!rowsFiltradas.length} title="Exportar a PDF">
            PDF
          </button>
          <button className="vta-btn-export" onClick={() => exportar('excel')}
            disabled={!rowsFiltradas.length} title="Exportar a Excel">
            Excel
          </button>
          <button className="vta-btn-icon" onClick={cargar} title="Actualizar lista">
            <FiRefreshCw size={14} />
          </button>
          {cajaInfo ? (
            <button className="vta-btn-nueva" onClick={onNuevaVenta}>
              <FiPlus size={14} /> Nueva Venta
            </button>
          ) : (
            <button className="vta-btn-apertura" onClick={() => navigate('/caja/apertura')}>
              Abrir Caja
            </button>
          )}
        </div>
      </div>

      {/* ── KPIs ── */}
      <div className="vta-kpi-bar">
        <div className="vta-kpi-card">
          <div className="vta-kpi-label">Ventas completadas</div>
          <div className="vta-kpi-value neutral">{completadas.length}</div>
        </div>
        <div className="vta-kpi-card">
          <div className="vta-kpi-label">{desde === hasta ? 'Total del día' : 'Total del rango'}</div>
          <div className="vta-kpi-value blue">S/ {fmtMoney(totalDia)}</div>
        </div>
        <div className="vta-kpi-card">
          <div className="vta-kpi-label">Contado</div>
          <div className="vta-kpi-value green">S/ {fmtMoney(totalContado)}</div>
        </div>
        <div className="vta-kpi-card">
          <div className="vta-kpi-label">Crédito</div>
          <div className="vta-kpi-value orange">S/ {fmtMoney(totalCredito)}</div>
        </div>
      </div>

      {/* ── Grid ── */}
      <div className="vta-grid-wrap">
        {loading ? (
          <div className="vta-grid-empty">Cargando...</div>
        ) : error ? (
          <div className="vta-grid-error">{error}</div>
        ) : rows.length === 0 ? (
          <div className="vta-grid-empty">
            {desde === hasta && desde === hoy() ? 'No hay ventas registradas hoy.' : 'No hay ventas registradas en el rango seleccionado.'}
            {cajaInfo && (
              <button className="vta-grid-empty-btn" onClick={onNuevaVenta}>
                <FiPlus size={13} /> Registrar primera venta
              </button>
            )}
          </div>
        ) : rowsFiltradas.length === 0 ? (
          <div className="vta-grid-empty">
            Sin ventas que coincidan con «{filtroCliente.trim()}».
          </div>
        ) : (
          <table className="vta-grid">
            <thead>
              <tr>
                <th>N° Venta</th>
                <th>Fecha / Hora</th>
                <th>Caja</th>
                <th>Almacén</th>
                <th>Cliente</th>
                <th style={{ textAlign: 'center' }}>Items</th>
                <th>Tipo</th>
                <th style={{ textAlign: 'right' }}>Total</th>
                <th>Estado</th>
                <th style={{ width: esAdmin ? 64 : 36 }}></th>
              </tr>
            </thead>
            <tbody>
              {rowsFiltradas.map((row, i) => {
                const badge   = estadoBadge(row.estado);
                const anulada = row.estado === 'ANULADA';
                return (
                  <tr key={row.id}
                      className={`${i % 2 === 0 ? 'vta-grid-even' : 'vta-grid-odd'}${anulada ? ' vta-grid-anulada' : ''}`}>
                    <td><strong>{row.numero}</strong></td>
                    <td className="vta-grid-hora">{fmtFecha(row.fecha)} {fmtHora(row.creado_en)}</td>
                    <td>{row.caja_nombre || '—'}</td>
                    <td>{row.almacen_nombre || '—'}</td>
                    <td>{row.cliente_nombre || <span className="vta-grid-cf">Clientes Varios</span>}</td>
                    <td style={{ textAlign: 'center' }}>{row.nro_items}</td>
                    <td>
                      <span className={`vta-tipo-badge ${row.condicion_pago === 'CONTADO' ? 'contado' : row.condicion_pago === 'MIXTO' ? 'mixto' : 'credito'}`}>
                        {row.condicion_pago}
                      </span>
                    </td>
                    <td style={{ textAlign: 'right', fontWeight: 700 }}>
                      S/ {fmtMoney(row.total)}
                    </td>
                    <td>
                      <span className={`vta-estado-badge ${badge.cls}`}>{badge.label}</span>
                    </td>
                    <td style={{ textAlign: 'center', whiteSpace: 'nowrap' }}>
                      <button
                        className="vta-btn-ver"
                        title="Ver detalle"
                        onClick={() => verDetalle(row.id)}
                      >
                        <IconEye size={14} />
                      </button>
                      {!anulada && (
                        <>
                          <button
                            className="vta-btn-print"
                            title="Reimprimir comprobante"
                            onClick={() => setPrintVenta({ id: row.id, numero: row.numero })}
                          >
                            <IconPrint size={14} />
                          </button>
                          <button
                            className="vta-btn-despacho"
                            title="Orden de despacho"
                            onClick={() => setDespachoVenta({ id: row.id, numero: row.numero })}
                          >
                            <IconTruck size={14} />
                          </button>
                          {esAdmin && (
                            <button
                              className="vta-btn-anular"
                              title="Anular venta"
                              onClick={() => { setErrAnular(''); setConfirmVenta({ id: row.id, numero: row.numero, total: row.total }); }}
                            >
                              <IconSlash size={14} />
                            </button>
                          )}
                        </>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>

      {/* ── Footer totalizador ── */}
      {!loading && rows.length > 0 && (
        <div className="vta-grid-footer">
          <span>{rows.length} venta{rows.length !== 1 ? 's' : ''} registrada{rows.length !== 1 ? 's' : ''}</span>
          <span>{desde === hasta ? 'Total del día' : 'Total del rango'}: <strong>S/ {fmtMoney(totalDia)}</strong></span>
        </div>
      )}

      {/* ── Modal detalle de venta ── */}
      {detalleVenta && (
        <div className="vta-overlay">
          <div className="vta-det-modal" onClick={e => e.stopPropagation()}>
            <div className="vta-print-header">
              <span className="vta-print-title"><IconEye size={15} /> Detalle de venta</span>
              <button className="vta-cobro-close" onClick={() => setDetalleVenta(null)}><FiX size={16} /></button>
            </div>
            <div className="vta-det-numero">
              {detalleVenta.numero}
              <span className={`vta-estado-badge ${estadoBadge(detalleVenta.estado).cls}`}>
                {estadoBadge(detalleVenta.estado).label}
              </span>
            </div>
            <div className="vta-det-grid">
              <div><b>Fecha:</b> {fmtFecha(detalleVenta.fecha)}{detalleVenta.creado_en ? ` ${fmtHora(detalleVenta.creado_en)}` : ''}</div>
              <div><b>Caja:</b> {detalleVenta.caja_nombre || '—'}</div>
              <div><b>Cliente:</b> {detalleVenta.cliente_nombre || 'Clientes Varios'}{detalleVenta.cliente_documento ? ` — ${detalleVenta.cliente_documento}` : ''}</div>
              <div><b>Vendedor:</b> {detalleVenta.vendedor_nombre || '—'}</div>
              <div><b>Documento:</b> {detalleVenta.documento_pago_nombre || '—'}{detalleVenta.serie ? ` ${detalleVenta.serie}` : ''}</div>
              <div><b>Pago:</b> {detalleVenta.condicion_pago}{detalleVenta.forma_pago_nombre ? ` — ${detalleVenta.forma_pago_nombre}` : ''}</div>
              {detalleVenta.condicion_pago !== 'CONTADO' && detalleVenta.fecha_vencimiento && (
                <div><b>Vence:</b> {fmtFecha(detalleVenta.fecha_vencimiento)}{detalleVenta.dias_credito ? ` (${detalleVenta.dias_credito} días)` : ''}</div>
              )}
              {detalleVenta.observacion && (
                <div className="vta-det-full"><b>Observación:</b> {detalleVenta.observacion}</div>
              )}
            </div>
            <div className="vta-det-tabla-wrap">
              <table className="vta-det-tabla">
                <thead>
                  <tr>
                    <th>Código</th><th>Producto</th><th>Alm.</th><th>Und</th>
                    <th className="vta-det-num">Cant.</th>
                    <th className="vta-det-num">P. Unit</th>
                    <th className="vta-det-num">Subtotal</th>
                  </tr>
                </thead>
                <tbody>
                  {(detalleVenta.lineas || []).map((l) => (
                    <tr key={l.id}>
                      <td>{l.codigo}</td>
                      <td>{l.producto_nombre}</td>
                      <td><span className="vta-cart-alm-tag" title={l.almacen_nombre}>{almacenTag(l.almacen_nombre)}</span></td>
                      <td>{l.unidad_short || '—'}</td>
                      <td className="vta-det-num">{Number(l.cantidad)}</td>
                      <td className="vta-det-num">{fmtMoney(l.precio_unitario)}</td>
                      <td className="vta-det-num">{fmtMoney(l.subtotal)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <div className="vta-det-tot">
              <div><span>Subtotal</span><span>S/ {fmtMoney(detalleVenta.subtotal)}</span></div>
              {Number(detalleVenta.descuento) > 0 && (
                <div><span>Descuento</span><span>- S/ {fmtMoney(detalleVenta.descuento)}</span></div>
              )}
              {Number(detalleVenta.igv) > 0 && (
                <div><span>IGV (18%)</span><span>S/ {fmtMoney(detalleVenta.igv)}</span></div>
              )}
              <div className="vta-det-tot-final"><span>Total</span><span>S/ {fmtMoney(detalleVenta.total)}</span></div>
            </div>
          </div>
        </div>
      )}

      {/* ── Modal reimprimir ── */}
      {printVenta && (
        <div className="vta-overlay">
          <div className="vta-print-modal" onClick={e => e.stopPropagation()}>
            <div className="vta-print-header">
              <span className="vta-print-title"><IconPrint size={15} /> Reimprimir comprobante</span>
              <button className="vta-cobro-close" onClick={() => setPrintVenta(null)}><FiX size={16} /></button>
            </div>
            <div className="vta-print-numero">{printVenta.numero}</div>
            <div className="vta-print-formats">
              <button className="vta-print-fmt" onClick={() => imprimir('a4')}>
                <span className="vta-print-fmt-name">A4</span>
                <span className="vta-print-fmt-desc">Hoja completa</span>
              </button>
              <button className="vta-print-fmt" onClick={() => imprimir('80')}>
                <span className="vta-print-fmt-name">80mm</span>
                <span className="vta-print-fmt-desc">Ticket térmico</span>
              </button>
              <button className="vta-print-fmt" onClick={() => imprimir('60')}>
                <span className="vta-print-fmt-name">60mm</span>
                <span className="vta-print-fmt-desc">Ticket angosto</span>
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── Modal orden de despacho ── */}
      {despachoVenta && (
        <div className="vta-overlay">
          <div className="vta-print-modal" onClick={e => e.stopPropagation()}>
            <div className="vta-print-header">
              <span className="vta-print-title"><IconTruck size={15} /> Orden de despacho</span>
              <button className="vta-cobro-close" onClick={() => setDespachoVenta(null)}><FiX size={16} /></button>
            </div>
            <div className="vta-print-numero">{despachoVenta.numero}</div>
            <div className="vta-print-formats">
              <button className="vta-print-fmt" onClick={() => imprimirDespacho('a4')}>
                <span className="vta-print-fmt-name">A4</span>
                <span className="vta-print-fmt-desc">Hoja completa</span>
              </button>
              <button className="vta-print-fmt" onClick={() => imprimirDespacho('80')}>
                <span className="vta-print-fmt-name">80mm</span>
                <span className="vta-print-fmt-desc">Ticket térmico</span>
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── Modal confirmación anular ── */}
      {confirmVenta && (
        <div className="vta-overlay">
          <div className="vta-confirm-modal" onClick={e => e.stopPropagation()}>
            <div className="vta-confirm-icon"><IconSlash size={28} /></div>
            <div className="vta-confirm-title">¿Anular esta venta?</div>
            <div className="vta-confirm-numero">{confirmVenta.numero}</div>
            <div className="vta-confirm-monto">S/ {fmtMoney(confirmVenta.total)}</div>
            <div className="vta-confirm-aviso">
              Se devolverá el stock a su almacén de origen y se afectará el total de caja.
              Esta acción no se puede deshacer.
            </div>
            {errAnular && <div className="vta-confirm-error">{errAnular}</div>}
            <div className="vta-confirm-actions">
              <button className="vta-confirm-btn-ok" onClick={handleAnular} disabled={anulando}>
                {anulando ? 'Anulando...' : 'Sí, anular'}
              </button>
              <button className="vta-confirm-btn-cancel" onClick={() => setConfirmVenta(null)} disabled={anulando}>
                Cancelar
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

/* ═══════════════════════════════════════════════════════════════════════════════
   POS — PANTALLA DE NUEVA VENTA
═══════════════════════════════════════════════════════════════════════════════ */
/* ═══════════════════════════════════════════════════════════════════════════════
   MODAL — Cotizaciones vigentes (seleccionar y cargar al POS)
═══════════════════════════════════════════════════════════════════════════════ */
function CotizacionesModal({ onSelect, cargando, onClose }) {
  const [lista,      setLista]      = useState([]);
  const [loading,    setLoading]    = useState(true);
  const [error,      setError]      = useState('');
  const [selId,      setSelId]      = useState(null);
  const [search,     setSearch]     = useState('');

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const r = await api.get('/api/cotizaciones', { params: { estado: 'VIGENTE' } });
        if (!cancelled) setLista(r.data?.data || []);
      } catch (e) {
        if (!cancelled) setError(e.response?.data?.error || 'Error al cargar las cotizaciones.');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const q = search.trim().toLowerCase();
  const filtradas = q
    ? lista.filter(c =>
        (c.numero || '').toLowerCase().includes(q) ||
        (c.cliente_nombre || '').toLowerCase().includes(q))
    : lista;

  return (
    <div className="vta-overlay">
      <div className="vta-cot-modal">
        <div className="vta-cot-modal-header">
          <h3>Cotizaciones vigentes</h3>
          <button className="vta-cot-modal-close" title="Cerrar" onClick={onClose}>
            <FiX size={16} />
          </button>
        </div>

        <input
          className="vta-cot-modal-search"
          type="text"
          placeholder="Buscar por número o cliente..."
          value={search}
          onChange={e => setSearch(e.target.value)}
          autoComplete="off"
        />

        {error && <div className="vta-error">{error}</div>}

        <div className="vta-cot-modal-list">
          {loading ? (
            <div className="vta-cot-modal-empty">Cargando...</div>
          ) : filtradas.length === 0 ? (
            <div className="vta-cot-modal-empty">
              {q ? 'Sin resultados para la búsqueda' : 'No hay cotizaciones vigentes'}
            </div>
          ) : (
            <table className="vta-cot-modal-table">
              <thead>
                <tr>
                  <th>Número</th>
                  <th>Fecha</th>
                  <th>Cliente</th>
                  <th style={{ textAlign: 'center' }}>Ítems</th>
                  <th style={{ textAlign: 'right' }}>Total</th>
                </tr>
              </thead>
              <tbody>
                {filtradas.map(c => (
                  <tr
                    key={c.id}
                    className={selId === c.id ? 'selected' : ''}
                    onClick={() => setSelId(c.id)}
                    onDoubleClick={() => onSelect(c.id)}
                  >
                    <td className="vta-cot-modal-num">{c.numero}</td>
                    <td>{fmtFecha(c.fecha)}</td>
                    <td>{c.cliente_nombre || '—'}</td>
                    <td style={{ textAlign: 'center' }}>{c.nro_items}</td>
                    <td style={{ textAlign: 'right' }}>S/ {fmtMoney(c.total)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>

        <div className="vta-cot-modal-footer">
          <button className="vta-cot-modal-btn-cancel" onClick={onClose}>Cancelar</button>
          <button
            className="vta-cot-modal-btn-ok"
            disabled={!selId || cargando}
            onClick={() => onSelect(selId)}
          >
            {cargando ? 'Cargando...' : 'Aceptar'}
          </button>
        </div>
      </div>
    </div>
  );
}

function PosPantalla({ cajaInfo, puedeModificarPrecio, onVolver }) {

  /* ── Búsqueda de productos ── */
  const [busqProd,     setBusqProd]     = useState('');
  const [productos,    setProductos]    = useState([]);
  const [buscandoProd, setBuscandoProd] = useState(false);
  const busqProdTimer                   = useRef(null);

  /* ── Carrito ── */
  const [carrito, setCarrito] = useState([]);

  /* ── Documento de pago + serie ── */
  const [series,       setSeries]       = useState([]);
  const [serieSelecta, setSerieSelecta] = useState(null);

  /* ── Formas de pago (YAPE, PLIN, etc.) ── */
  const [formasPago, setFormasPago] = useState([]);
  const [formaPago,  setFormaPago]  = useState(null);

  /* ── Opciones de venta ── */
  const [incluirIgv,  setIncluirIgv]  = useState(false);
  const [condicion,   setCondicion]   = useState('CONTADO');
  const [diasCredito, setDiasCredito] = useState(30);
  const [observacion, setObservacion] = useState('');
  const [fecha,       setFecha]       = useState(hoy());

  /* ── Cliente ── */
  const [busqCliente,     setBusqCliente]     = useState('');
  const [clienteOpciones, setClienteOpciones] = useState([]);
  const [buscandoCliente, setBuscandoCliente] = useState(false);
  const [clienteSelecto,  setClienteSelecto]  = useState(null);
  const [showClienteDrop, setShowClienteDrop] = useState(false);
  const busqClienteTimer                       = useRef(null);
  const clienteInputRef                        = useRef(null);

  /* ── Descuento (monto en soles) ── */
  const [descuento, setDescuento] = useState('');

  /* ── Cotizaciones (cargar cotización en la venta) ── */
  const [showCotModal,  setShowCotModal]  = useState(false);
  const [cotizacionRef, setCotizacionRef] = useState(null);
  const [cargandoCot,   setCargandoCot]   = useState(false);

  /* ── Cobro ── */
  const [showCobro,    setShowCobro]    = useState(false);
  const [cobrando,     setCobrando]     = useState(false);
  const [errCobro,     setErrCobro]     = useState('');
  const [ventaOk,      setVentaOk]      = useState(null);
  const [montoContado, setMontoContado] = useState(''); // parte al contado en venta MIXTO

  /* Cerrar dropdown cliente al click fuera */
  const clienteWrapRef = useRef(null);
  useEffect(() => {
    const h = (e) => {
      if (clienteWrapRef.current && !clienteWrapRef.current.contains(e.target))
        setShowClienteDrop(false);
    };
    document.addEventListener('mousedown', h);
    return () => document.removeEventListener('mousedown', h);
  }, []);

  /* ── Búsqueda de productos ── */
  const buscarProductos = useCallback(async (q) => {
    if (!cajaInfo) { setProductos([]); return; }
    if (q && q.trim().length > 0 && q.trim().length < 2) return; // esperar mínimo 2 chars al escribir
    setBuscandoProd(true);
    try {
      const r = await api.get('/api/ventas/productos-busqueda', {
        params: { q: q || '', caja_id: cajaInfo.id },
      });
      setProductos(r.data?.data || []);
    } catch { setProductos([]); }
    finally { setBuscandoProd(false); }
  }, [cajaInfo]);

  // Carga/recarga de series (la secuencia avanza con cada venta)
  const cargarSeries = useCallback(async (autoSelect = false) => {
    try {
      const r = await api.get('/api/config/series-venta');
      const list = r.data?.data || [];
      setSeries(list);
      if (autoSelect && list.length === 1) setSerieSelecta(list[0]);
      else setSerieSelecta(prev => prev ? (list.find(s => s.id === prev.id) || null) : prev);
      return list;
    } catch { return []; }
  }, []);

  // Carga inicial: productos, series de venta y formas de pago cuando la caja esté lista
  useEffect(() => {
    if (!cajaInfo) return;
    buscarProductos('');
    cargarSeries(true);
    api.get('/api/config/formas-pago').then(r => {
      // CRÉDITO es condición de venta, no modalidad de cobro al contado
      setFormasPago((r.data?.data || []).filter(f => f.short_name !== 'CREDITO'));
    }).catch(() => {});
  }, [cajaInfo, buscarProductos, cargarSeries]);

  const handleBusqProd = (v) => {
    setBusqProd(v);
    clearTimeout(busqProdTimer.current);
    if (!v) {
      busqProdTimer.current = setTimeout(() => buscarProductos(''), 200);
      return;
    }
    if (v.trim().length < 2) return;
    setBuscandoProd(true);
    busqProdTimer.current = setTimeout(() => buscarProductos(v), 300);
  };

  /* ── Carrito: agregar ── */
  const agregarAlCarrito = (row) => {
    const key        = cartKey(row.producto_id, row.almacen_id);
    const permiteNeg  = !!row.permite_stock_negativo;
    const stockDisp   = parseFloat(row.stock) || 0;
    const opciones    = buildOpciones(row);
    const opcion      = opcionDefault(opciones);

    setCarrito(prev => {
      const existente = prev.find(i => i._key === key);
      if (existente) {
        const maxCant = existente.permite_stock_negativo ? Infinity : existente.stock_disponible / existente.factor;
        const nueva   = existente.cantidad + 1;
        return prev.map(i => i._key === key ? { ...i, cantidad: Math.min(nueva, maxCant) } : i);
      }
      return [
        ...prev,
        {
          _key:                   key,
          producto_id:            row.producto_id,
          almacen_id:             row.almacen_id,
          almacen_tag:            almacenTag(row.almacen_nombre),
          almacen_nombre:         row.almacen_nombre,
          nombre:                 row.nombre,
          codigo:                 row.codigo || '',
          opciones,
          opcionKey:              opcion.key,
          factor:                 opcion.factor,
          unidad_nombre:          opcion.nombre,
          precio_unitario:        opcion.precio,
          custom:                 false,
          cantidad:               1,
          stock_disponible:       stockDisp,
          permite_stock_negativo: permiteNeg,
        },
      ];
    });
  };

  /* ── Carrito: modificar ── */
  const setCantidad = (key, rawVal) => {
    const val = parseFloat(rawVal);
    if (isNaN(val) || val <= 0) { setCarrito(prev => prev.filter(i => i._key !== key)); return; }
    setCarrito(prev => prev.map(i => {
      if (i._key !== key) return i;
      const maxCant = i.permite_stock_negativo ? Infinity : i.stock_disponible / i.factor;
      return { ...i, cantidad: Math.min(val, maxCant) };
    }));
  };

  const setPrecio = (key, rawVal) => {
    const val = parseFloat(rawVal);
    if (isNaN(val) || val < 0) return;
    setCarrito(prev => prev.map(i =>
      i._key === key ? { ...i, precio_unitario: val, custom: true } : i
    ));
  };

  /* Cambiar la presentación/unidad de venta de una línea (UNIDAD, CAJA minorista/mayorista, fracción) */
  const handleOpcion = (key, opcionKey) => {
    setCarrito(prev => prev.map(i => {
      if (i._key !== key) return i;
      const opcion = i.opciones.find(o => o.key === opcionKey);
      if (!opcion) return i;
      const maxCant = i.permite_stock_negativo ? Infinity : i.stock_disponible / opcion.factor;
      return {
        ...i,
        opcionKey:     opcion.key,
        factor:        opcion.factor,
        unidad_nombre: opcion.nombre,
        precio_unitario: opcion.precio,
        custom:        false,
        cantidad:      Math.min(i.cantidad, maxCant),
      };
    }));
  };

  const quitarDelCarrito = (key) => setCarrito(prev => prev.filter(i => i._key !== key));

  /* ── Cargar una cotización al POS ── */
  const cargarCotizacion = async (cotId) => {
    setCargandoCot(true);
    try {
      const r = await api.get(`/api/cotizaciones/${cotId}`);
      const cot = r.data?.data;
      if (!cot) throw new Error('No se pudo cargar la cotización.');
      if (cot.estado !== 'VIGENTE') throw new Error('La cotización ya no está vigente.');

      const items = [];
      for (const l of cot.lineas || []) {
        // Buscar el producto en la caja para conocer stock, almacén y precios actuales
        let row = null;
        try {
          const q = (l.codigo || l.producto_nombre || '').trim();
          const rb = await api.get('/api/ventas/productos-busqueda', {
            params: { q, caja_id: cajaInfo.id },
          });
          const rows = (rb.data?.data || []).filter(p => p.producto_id === l.producto_id);
          row = rows.find(p => p.almacen_id === cajaInfo.almacen_id) || rows[0] || null;
        } catch (_) { /* sin datos de stock: usar fallback */ }

        const cant      = parseFloat(l.cantidad) || 1;
        const almId     = row?.almacen_id || cajaInfo.almacen_id;
        const almNombre = row?.almacen_nombre || cajaInfo.almacen_nombre || '';
        const opciones  = row
          ? buildOpciones(row)
          : [{ key: 'minima', nombre: l.unidad_short || 'UND', factor: 1, precio: parseFloat(l.precio_unitario) || 0 }];
        const factor    = parseFloat(l.factor) || 1;
        // Intenta reconocer la presentación con la que se cotizó; si no calza, usa la de por defecto
        const opcion    = opciones.find(o => o.nombre === l.unidad_short && Math.abs(o.factor - factor) < 0.0001)
                        || opcionDefault(opciones);
        items.push({
          _key:                   cartKey(l.producto_id, almId),
          producto_id:            l.producto_id,
          almacen_id:             almId,
          almacen_tag:            almacenTag(almNombre),
          almacen_nombre:         almNombre,
          nombre:                 l.producto_nombre,
          codigo:                 l.codigo || '',
          opciones,
          opcionKey:              opcion.key,
          factor,
          unidad_nombre:          l.unidad_short || opcion.nombre,
          precio_unitario:        parseFloat(l.precio_unitario) || 0,
          custom:                 true, // conserva el precio exacto cotizado, sin recalcular
          cantidad:               cant,
          stock_disponible:       row ? (parseFloat(row.stock) || 0) : cant * factor,
          permite_stock_negativo: !!row?.permite_stock_negativo,
        });
      }

      setCarrito(items);
      setClienteSelecto(cot.cliente_id ? {
        id:              cot.cliente_id,
        nombre_completo: cot.cliente_nombre,
        tipo_documento:  cot.cliente_tipo_documento || '',
        nro_documento:   cot.cliente_documento || '',
      } : null);
      const descCot = parseFloat(cot.descuento) || 0;
      setDescuento(descCot > 0 ? String(descCot) : '');
      setIncluirIgv(!!cot.incluir_igv);
      setObservacion(`Ref. cotización ${cot.numero}`);
      setCotizacionRef({ id: cot.id, numero: cot.numero });
      setShowCotModal(false);
      setErrCobro('');
    } catch (e) {
      setErrCobro(e.response?.data?.error || e.message || 'Error al cargar la cotización.');
      setShowCotModal(false);
    } finally {
      setCargandoCot(false);
    }
  };

  /* ── Totales (descuento monetario, tope = subtotal) ── */
  const subtotal       = carrito.reduce((s, i) => s + i.cantidad * i.precio_unitario, 0);
  const descuentoMonto = Math.min(
    Math.max(0, Math.round((parseFloat(descuento) || 0) * 100) / 100),
    Math.round(subtotal * 100) / 100
  );
  const baseIgv        = subtotal - descuentoMonto;
  const igvMonto       = incluirIgv ? Math.round(baseIgv * 0.18 * 100) / 100 : 0;
  const total          = Math.round((baseIgv + igvMonto) * 100) / 100;

  // MIXTO se comporta como crédito: cliente requerido, días de crédito y vencimiento
  const esCredito = condicion === 'CRÉDITO' || condicion === 'MIXTO';

  const fechaVencimiento = esCredito && diasCredito > 0
    ? addDias(fecha, parseInt(diasCredito) || 30)
    : null;

  /* ── Búsqueda de clientes ── */
  const buscarClientes = useCallback(async (q) => {
    if (!q || q.trim().length < 2) { setClienteOpciones([]); return; }
    setBuscandoCliente(true);
    try {
      const r = await api.get('/api/clientes', { params: { search: q } });
      setClienteOpciones(r.data?.data || []);
      setShowClienteDrop(true);
    } catch { setClienteOpciones([]); }
    finally { setBuscandoCliente(false); }
  }, []);

  const handleBusqCliente = (v) => {
    setBusqCliente(v);
    clearTimeout(busqClienteTimer.current);
    if (!v || v.trim().length < 2) { setClienteOpciones([]); setShowClienteDrop(false); return; }
    busqClienteTimer.current = setTimeout(() => buscarClientes(v), 300);
  };

  const seleccionarCliente   = (cli) => { setClienteSelecto(cli); setBusqCliente(''); setClienteOpciones([]); setShowClienteDrop(false); };
  const deseleccionarCliente = () => { setClienteSelecto(null); setBusqCliente(''); setTimeout(() => clienteInputRef.current?.focus(), 50); };

  /* ── Cobrar: paso 1 — abrir modal de confirmación ── */
  const handleCobrar = () => {
    setErrCobro('');
    if (!cajaInfo)              { setErrCobro('No hay caja abierta.'); return; }
    if (carrito.length === 0)   { setErrCobro('El carrito está vacío.'); return; }
    if (esCredito && !clienteSelecto) {
      setErrCobro(`Para ventas ${condicion === 'MIXTO' ? 'mixtas' : 'a crédito'} debes seleccionar un cliente.`); return;
    }
    if (esCredito && (clienteSelecto?.nombre_completo || '').toUpperCase().includes('CLIENTES VARIOS')) {
      setErrCobro(`Las ventas ${condicion === 'MIXTO' ? 'mixtas' : 'a crédito'} requieren un cliente identificado, no "Clientes Varios".`); return;
    }
    setMontoContado('');
    setShowCobro(true);
  };

  /* ── Cobrar: paso 2 — confirmar y generar la venta ── */
  const confirmarVenta = async () => {
    setErrCobro('');
    if (!serieSelecta) { setErrCobro('Selecciona el tipo de documento de pago.'); return; }
    if (condicion !== 'CRÉDITO' && !formaPago) {
      setErrCobro('Selecciona la forma de pago.'); return;
    }
    const mContado = Math.round((parseFloat(montoContado) || 0) * 100) / 100;
    if (condicion === 'MIXTO') {
      if (mContado <= 0)     { setErrCobro('Ingresa el monto pagado al contado.'); return; }
      if (mContado >= total) { setErrCobro('El monto al contado debe ser menor al total; si paga todo, usa CONTADO.'); return; }
    }
    setCobrando(true);
    try {
      const r = await api.post('/api/ventas', {
        caja_id:        cajaInfo.id,
        almacen_id:     cajaInfo.almacen_id,
        sesion_id:      cajaInfo.sesion_id,
        cliente_id:     clienteSelecto?.id || null,
        condicion_pago: condicion,
        dias_credito:   esCredito ? parseInt(diasCredito) || 30 : null,
        fecha,
        incluir_igv:    incluirIgv,
        descuento:      descuentoMonto,
        observacion:    observacion.trim() || null,
        serie_venta_id: serieSelecta?.id || null,
        forma_pago_id:  condicion !== 'CRÉDITO' ? (formaPago?.id || null) : null,
        monto_contado:  condicion === 'MIXTO' ? mContado : null,
        cotizacion_id:  cotizacionRef?.id || null,
        lineas: carrito.map(i => ({
          producto_id:                  i.producto_id,
          almacen_id:                   i.almacen_id,
          // cantidad/precio_unitario siempre convertidos a Unidad Mínima (stock/kardex/costo)
          cantidad:                     Math.round(i.cantidad * i.factor * 10000) / 10000,
          precio_unitario:              Math.round((i.precio_unitario / i.factor) * 10000) / 10000,
          // metadata de la presentación elegida, solo para mostrar en tickets/reportes
          cantidad_presentacion:        i.cantidad,
          precio_unitario_presentacion: i.precio_unitario,
          factor:                       i.factor,
          unidad_nombre:                i.unidad_nombre,
        })),
      });
      if (r.data?.success) {
        setShowCobro(false);
        setVentaOk({
          id:                r.data.id,
          numero:            r.data.numero,
          total:             r.data.total,
          subtotal:          r.data.subtotal,
          descuento:         r.data.descuento,
          igv:               r.data.igv,
          condicion_pago:    condicion,
          forma_pago:        condicion !== 'CRÉDITO' ? (formaPago?.name || null) : null,
          monto_contado:     condicion === 'MIXTO' ? mContado : null,
          documento:         serieSelecta ? `${serieSelecta.doc_name}` : null,
          cliente_nombre:    clienteSelecto?.nombre_completo || 'Clientes Varios',
          fecha_vencimiento: fechaVencimiento,
        });
        cargarSeries(); // la secuencia avanzó — refrescar correlativo
      } else {
        setErrCobro(r.data?.error || 'Error al registrar la venta.');
      }
    } catch (e) {
      setErrCobro(e.response?.data?.error || e.message || 'Error al registrar la venta.');
    } finally {
      setCobrando(false);
    }
  };

  /* ── Reset ── */
  const nuevaVenta = () => {
    setVentaOk(null); setCarrito([]); setBusqProd(''); setProductos([]);
    setIncluirIgv(false); setDescuento(''); setCondicion('CONTADO');
    setDiasCredito(30); setObservacion(''); setFecha(hoy());
    setClienteSelecto(null); setBusqCliente(''); setClienteOpciones([]); setErrCobro('');
    setFormaPago(null); setShowCobro(false); setCotizacionRef(null); setMontoContado('');
    if (series.length === 1) setSerieSelecta(series[0]);
    else if (series.length > 1) setSerieSelecta(null);
  };

  return (
    <div className="vta-page">
      {/* ── Header POS ── */}
      <div className="vta-header">
        <div className="vta-header-left">
          <button className="vta-btn-volver" onClick={onVolver} title="Volver a listado">
            <FiArrowLeft size={14} /> Ventas
          </button>
          <div className="vta-header-title">
            <FiShoppingCart size={14} style={{ marginRight: 6, verticalAlign: 'middle' }} />
            Nueva Venta
          </div>
        </div>
        <div className="vta-header-info">
          <button
            className="vta-btn-cotizaciones"
            onClick={() => setShowCotModal(true)}
            disabled={cargandoCot}
            title="Cargar una cotización vigente a la venta"
          >
            <IconDoc size={14} /> {cargandoCot ? 'Cargando...' : 'Cotizaciones'}
          </button>
          {cotizacionRef && (
            <span className="vta-cot-ref-badge" title="Venta generada desde cotización">
              {cotizacionRef.numero}
            </span>
          )}
          <span><strong>Caja:</strong> {cajaInfo.nombre}</span>
          <span><strong>Almacén:</strong> {cajaInfo.almacen_nombre || '—'}</span>
          <span><strong>Fecha:</strong> {fmtFecha(fecha)}</span>
          <span className="vta-header-badge">SESIÓN ABIERTA</span>
        </div>
      </div>

      {/* ── Body POS ── */}
      <div className="vta-body">

        {/* ── Panel izquierdo: búsqueda ── */}
        <div className="vta-left">
          <div className="vta-left-inner">
            <div className="vta-search-wrap">
              <span className="vta-search-icon"><FiSearch size={16} /></span>
              <input
                className="vta-search-input"
                type="text"
                placeholder="Buscar producto por nombre o código..."
                value={busqProd}
                onChange={e => handleBusqProd(e.target.value)}
                autoComplete="off"
              />
              {buscandoProd && <span className="vta-search-spinner" />}
            </div>

            <div className="vta-prod-list">
              {!busqProd && productos.length > 0 && (
                <div className="vta-prod-hint" style={{ color: '#64748b', fontSize: 11, marginBottom: 4 }}>
                  Productos — {cajaInfo?.almacen_nombre || 'almacén default'}
                </div>
              )}
              {buscandoProd ? null : busqProd.trim().length === 1 ? (
                <div className="vta-prod-hint">Escribe al menos 2 caracteres para buscar</div>
              ) : productos.length === 0 ? (
                busqProd.trim().length >= 2
                  ? <div className="vta-prod-empty">No se encontraron productos para "{busqProd}"</div>
                  : null
              ) : (
                productos.map((prod, idx) => {
                  const stock      = parseFloat(prod.stock) || 0;
                  const sinStock   = stock <= 0;
                  const permiteNeg = !!prod.permite_stock_negativo;
                  const ref        = precioReferencial(prod);
                  return (
                    <div key={`${prod.producto_id}_${prod.almacen_id}_${idx}`} className="vta-prod-card">
                      <span className="vta-alm-tag" title={prod.almacen_nombre}>
                        {almacenTag(prod.almacen_nombre)}
                      </span>
                      <div className="vta-prod-info">
                        <div className="vta-prod-nombre">{prod.nombre}</div>
                        <div className="vta-prod-meta">
                          {prod.codigo && <span>Cód: {prod.codigo}</span>}
                          <span className={`vta-prod-stock${sinStock ? ' sin-stock' : ''}`}>
                            {sinStock ? 'Sin stock' : `Stock: ${stock} ${prod.unidad_short || 'UND'}`}
                          </span>
                          {sinStock && permiteNeg && (
                            <span className="vta-led-neg" title="Venta con stock negativo habilitada">
                              <span className="vta-led-neg-dot" />
                              Habilitado
                            </span>
                          )}
                        </div>
                      </div>
                      <div className="vta-prod-precio">S/ {fmtMoney(ref.precio)} <small>/ {ref.unidad}</small></div>
                      <button className="vta-prod-btn" onClick={() => agregarAlCarrito(prod)}>
                        + Agregar
                      </button>
                    </div>
                  );
                })
              )}
            </div>
          </div>
        </div>

        {/* ── Panel derecho: carrito ── */}
        <div className="vta-right">
          <div className="vta-right-inner">
            <div className="vta-section-label">Carrito</div>

            {/* Selector Documento de Pago + Serie */}
            {series.length > 0 && (
              <div className="vta-doc-selector-wrap">
                <select
                  className="vta-doc-selector"
                  value={serieSelecta?.id || ''}
                  onChange={e => {
                    const sel = series.find(s => s.id === Number(e.target.value));
                    setSerieSelecta(sel || null);
                  }}
                >
                  <option value="">— Tipo de documento —</option>
                  {series.map(s => (
                    <option key={s.id} value={s.id}>
                      {s.doc_short} {s.serie} — {s.doc_name}
                    </option>
                  ))}
                </select>
                {serieSelecta && (
                  <span className="vta-doc-badge" title="Próximo número correlativo">
                    {serieSelecta.doc_short} {proxNumero(serieSelecta)}
                  </span>
                )}
              </div>
            )}

            {carrito.length === 0 ? (
              <div className="vta-cart-empty">No hay productos en el carrito</div>
            ) : (
              <table className="vta-cart-table">
                <thead>
                  <tr>
                    <th>#</th>
                    <th>Producto</th>
                    <th>Cant.</th>
                    <th>Precio</th>
                    <th style={{ textAlign: 'right' }}>Sub.</th>
                    <th></th>
                  </tr>
                </thead>
                <tbody>
                  {carrito.map((item, idx) => (
                    <tr key={item._key}>
                      <td className="vta-cart-num">{idx + 1}</td>
                      <td>
                        <div className="vta-cart-name">{item.nombre}</div>
                        <div className="vta-cart-name-sub">
                          <span className="vta-cart-alm-tag">{item.almacen_tag}</span>
                          {' '}{item.unidad_nombre}
                        </div>
                      </td>
                      <td>
                        <input
                          className="vta-cart-qty-input"
                          type="number" min="0.01" step="0.01"
                          max={item.permite_stock_negativo ? undefined : (item.stock_disponible / item.factor)}
                          value={item.cantidad}
                          onChange={e => setCantidad(item._key, e.target.value)}
                          onBlur={e => { if (!e.target.value || parseFloat(e.target.value) <= 0) quitarDelCarrito(item._key); }}
                        />
                      </td>
                      <td>
                        <div className="vta-precio-group">
                          <select
                            className="vta-precio-select"
                            value={item.custom ? '__custom__' : item.opcionKey}
                            onChange={e => { if (e.target.value !== '__custom__') handleOpcion(item._key, e.target.value); }}
                          >
                            {item.opciones.map(o => (
                              <option key={o.key} value={o.key}>{o.nombre}{o.label ? ` (${o.label})` : ''}</option>
                            ))}
                            {item.custom && <option value="__custom__">Personalizado</option>}
                          </select>
                          {puedeModificarPrecio ? (
                            <input
                              className="vta-cart-precio-input"
                              type="number" min="0" step="0.01"
                              value={item.precio_unitario}
                              onChange={e => setPrecio(item._key, e.target.value)}
                            />
                          ) : (
                            <span className="vta-precio-display">S/ {item.precio_unitario.toFixed(2)}</span>
                          )}
                        </div>
                      </td>
                      <td className="vta-cart-subtotal">
                        S/ {fmtMoney(item.cantidad * item.precio_unitario)}
                      </td>
                      <td>
                        <button className="vta-cart-btn-del" title="Quitar" onClick={() => quitarDelCarrito(item._key)}>
                          <FiX size={13} />
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}

            {/* Totales */}
            <div className="vta-totales">
              <div className="vta-subtotal-row">
                <span>Subtotal</span>
                <span className="vta-subtotal-amount">S/ {fmtMoney(subtotal)}</span>
              </div>
              <div className="vta-descuento-row">
                <label className="vta-descuento-label">
                  Descuento
                  <div className="vta-descuento-input-wrap">
                    <span className="vta-descuento-pct-sym">S/</span>
                    <input
                      type="number" className="vta-descuento-input"
                      min="0" step="0.01" placeholder="0.00"
                      value={descuento}
                      onChange={e => setDescuento(e.target.value)}
                    />
                  </div>
                </label>
                <span className="vta-descuento-amount">
                  {descuentoMonto > 0 ? `- S/ ${fmtMoney(descuentoMonto)}` : '—'}
                </span>
              </div>
              <div className="vta-igv-row">
                <label className="vta-igv-label">
                  <input type="checkbox" checked={incluirIgv} onChange={e => setIncluirIgv(e.target.checked)} />
                  Incluir IGV 18%
                </label>
                <span className="vta-igv-amount">S/ {fmtMoney(igvMonto)}</span>
              </div>
              <div className="vta-total-row">
                <span className="vta-total-label">TOTAL</span>
                <span className="vta-total-amount">S/ {fmtMoney(total)}</span>
              </div>
            </div>

            <div className="vta-divider" />

            {/* Cliente */}
            <div className="vta-section-label">Cliente</div>
            <div className="vta-cliente-wrap" ref={clienteWrapRef}>
              {clienteSelecto ? (
                <div className="vta-cliente-selected">
                  <div style={{ overflow: 'hidden' }}>
                    <span className="vta-cliente-selected-name">{clienteSelecto.nombre_completo}</span>
                    <span className="vta-cliente-selected-doc">
                      ({clienteSelecto.tipo_documento}: {clienteSelecto.nro_documento})
                    </span>
                  </div>
                  <button className="vta-cliente-btn-clear" title="Quitar cliente" onClick={deseleccionarCliente}>
                    <FiX size={14} />
                  </button>
                </div>
              ) : (
                <>
                  <input
                    ref={clienteInputRef}
                    className="vta-cliente-input"
                    type="text"
                    placeholder={esCredito ? 'Buscar cliente (requerido)...' : 'Buscar cliente (opcional)...'}
                    value={busqCliente}
                    onChange={e => handleBusqCliente(e.target.value)}
                    onFocus={() => { if (clienteOpciones.length > 0) setShowClienteDrop(true); }}
                    autoComplete="off"
                  />
                  {buscandoCliente && <span className="vta-cliente-spinner" />}
                  {showClienteDrop && clienteOpciones.length > 0 && (
                    <div className="vta-cliente-dropdown">
                      {clienteOpciones.map(cli => (
                        <div key={cli.id} className="vta-cliente-option" onMouseDown={() => seleccionarCliente(cli)}>
                          {cli.nombre_completo}
                          <span className="vta-cliente-option-doc">
                            {cli.tipo_documento}: {cli.nro_documento}
                          </span>
                        </div>
                      ))}
                    </div>
                  )}
                </>
              )}
            </div>

            <div className="vta-divider" />

            {/* Condición de pago */}
            <div className="vta-pago-row">
              <span className="vta-pago-label">Pago</span>
              <div className="vta-pago-options">
                {['CONTADO', 'CRÉDITO', 'MIXTO'].map(op => (
                  <label key={op} className={`vta-pago-radio${condicion === op ? ' active' : ''}`}>
                    <input type="radio" name="condicion_pago" value={op}
                      checked={condicion === op} onChange={() => setCondicion(op)} />
                    {op}
                  </label>
                ))}
              </div>
            </div>

            {esCredito && (
              <div className="vta-credito-row">
                <span className="vta-credito-label">Días crédito:</span>
                <input
                  className="vta-credito-input" type="number" min="1" max="365"
                  value={diasCredito} onChange={e => setDiasCredito(e.target.value)}
                />
                {fechaVencimiento && (
                  <span className="vta-credito-vence">Vence: {fmtFecha(fechaVencimiento)}</span>
                )}
              </div>
            )}

            <textarea
              className="vta-obs-input"
              placeholder="Observación (opcional)"
              value={observacion}
              onChange={e => setObservacion(e.target.value)}
              maxLength={200}
            />

            {errCobro && <div className="vta-error">{errCobro}</div>}

            <button
              className="vta-btn-cobrar"
              disabled={carrito.length === 0 || cobrando}
              onClick={handleCobrar}
            >
              {cobrando ? 'Procesando...' : `COBRAR  S/ ${fmtMoney(total)}`}
            </button>
          </div>
        </div>
      </div>

      {/* Modal de cotizaciones vigentes */}
      {showCotModal && (
        <CotizacionesModal
          onSelect={cargarCotizacion}
          cargando={cargandoCot}
          onClose={() => setShowCotModal(false)}
        />
      )}

      {/* Modal de confirmación de cobro */}
      {showCobro && (
        <CobrarModal
          total={total}
          condicion={condicion}
          clienteNombre={clienteSelecto?.nombre_completo || null}
          series={series}
          serieSelecta={serieSelecta}
          onSelectSerie={setSerieSelecta}
          formasPago={formasPago}
          formaPago={formaPago}
          onSelectFormaPago={setFormaPago}
          montoContado={montoContado}
          onMontoContado={setMontoContado}
          err={errCobro}
          cobrando={cobrando}
          onConfirm={confirmarVenta}
          onClose={() => { setShowCobro(false); setErrCobro(''); }}
        />
      )}

      {/* Modal ticket */}
      {ventaOk && (
        <TicketModal
          venta={ventaOk}
          onNuevaVenta={nuevaVenta}
          onVerVentas={() => { setVentaOk(null); onVolver(); }}
        />
      )}
    </div>
  );
}

/* ═══════════════════════════════════════════════════════════════════════════════
   VENTAS PAGE — router entre lista y POS
═══════════════════════════════════════════════════════════════════════════════ */
export default function VentasPage() {
  const navigate = useNavigate();
  const [loadingCaja,          setLoadingCaja]          = useState(true);
  const [cajaInfo,             setCajaInfo]             = useState(null);
  const [puedeModificarPrecio, setPuedeModificarPrecio] = useState(true);
  const [vista,                setVista]                = useState('lista');

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const [rCaja, rPerm] = await Promise.allSettled([
          api.get('/api/cajas/estado'),
          api.get('/api/caja-config/mi-permiso'),
        ]);
        if (cancelled) return;
        if (rCaja.status === 'fulfilled') {
          const cajas = rCaja.value.data?.data || [];
          const conSesion = cajas.find(c => c.sesion_activa);
          if (conSesion) {
            setCajaInfo({
              id:             conSesion.id,
              nombre:         conSesion.nombre,
              sesion_id:      conSesion.sesion_activa.id,
              almacen_id:     conSesion.sesion_activa.almacen_id,
              almacen_nombre: conSesion.sesion_activa.almacen_nombre,
            });
          }
        }
        if (rPerm.status === 'fulfilled') {
          setPuedeModificarPrecio(rPerm.value.data?.puede_modificar_precio !== false);
        }
      } catch (_) {}
      finally { if (!cancelled) setLoadingCaja(false); }
    })();
    return () => { cancelled = true; };
  }, []);

  if (loadingCaja) {
    return (
      <div className="vta-page">
        <div className="vta-loading"><div className="vta-loading-spinner" /> Cargando...</div>
      </div>
    );
  }

  if (vista === 'pos') {
    if (!cajaInfo) {
      return (
        <div className="vta-page">
          <SinCaja navigate={navigate} />
        </div>
      );
    }
    return (
      <PosPantalla
        cajaInfo={cajaInfo}
        puedeModificarPrecio={puedeModificarPrecio}
        onVolver={() => setVista('lista')}
      />
    );
  }

  return (
    <ListaVentas
      cajaInfo={cajaInfo}
      onNuevaVenta={() => setVista('pos')}
    />
  );
}
